import React, { useState, useEffect } from 'react';
import intl from 'react-intl-universal';
import styles from './PricingTableHostForm.module.css'; // Import CSS module

const MIN_COL_WIDTH = 260; // px — minimum width per tier column
const LABEL_COL_WIDTH = 220; // px — fixed width for the package column

const PricingTableHostForm = ({ productData, selectedPricingTable, pricingModelData, pricingModelCurrency }) => {
  const [rows, setRows] = useState([]);
  const [columns, setColumns] = useState([]);

  const isGlobalCountry =
    (pricingModelData && pricingModelData.country === 'global') ||
    (selectedPricingTable && (selectedPricingTable.country === 'global' || selectedPricingTable.pricingModelCountry === 'global'));

  const isAllowedGlobalProduct = product => {
    if (!isGlobalCountry) return true;
    const functions = (product && product.functions) || [];
    const hasSubscriptionAddOn = functions.includes('subscriptionAddOn');
    const hasOnlyHostManage = functions.length === 1 && functions[0] === 'hostManage';
    return hasSubscriptionAddOn || hasOnlyHostManage;
  };

  useEffect(() => {
    if (selectedPricingTable && selectedPricingTable.pricingCriteria && selectedPricingTable.products) {
      const pricingCriteriaKeys = Object.keys(selectedPricingTable.pricingCriteria);
      const visibleProductIndexes = selectedPricingTable.products
        .map((pricingProduct, index) => ({ pricingProduct, index }))
        .filter(({ pricingProduct }) => {
          const product = (productData || []).find(p => p._id === pricingProduct.product);
          return product && isAllowedGlobalProduct(product);
        })
        .map(({ index }) => index);
      setRows(visibleProductIndexes);
      setColumns(pricingCriteriaKeys);
    } else {
      setRows([]);
      setColumns([]);
    }
  }, [selectedPricingTable, productData]);

  const getProductName = productIndex => {
    const pricingTableProduct = selectedPricingTable.products[productIndex];
    if (!pricingTableProduct) return;
    const productId = pricingTableProduct.product;
    const product = (productData || []).find(p => p._id === productId);
    return product ? product.name : null;
  };

  const getPricingCell = (productIndex, tierIndex) => {
    const product = selectedPricingTable.products[productIndex];
    if (!product || !product.tiers) return null;

    const tiers = Object.values(product.tiers);
    const tier = tiers[tierIndex] || {};
    const monthPrice = tier.monthPrice != null ? tier.monthPrice : 'N/A';
    const yearPrice = tier.yearPrice != null ? tier.yearPrice : 'N/A';

    return (
      <div style={{ padding: '8px', lineHeight: '1.8', display: 'flex', gap: '48px', justifyContent: 'center' }}>
        <div>
          {pricingModelCurrency} {monthPrice} {intl.get('host.pricingTableForm.perMonth').d('/month')}
        </div>
        <div>|</div>
        <div>
          {pricingModelCurrency} {yearPrice} {intl.get('host.pricingTableForm.perYear').d('/year')}
        </div>
      </div>
    );
  };

  const getTierHeader = tierKey => {
    const { minUnit, maxUnit } = selectedPricingTable.pricingCriteria[tierKey] || {};
    const maxLabel = maxUnit != null ? maxUnit : intl.get('host.pricingTableForm.max').d('Max');
    return (
      <div style={{ padding: '8px' }}>
        {minUnit} {intl.get('host.pricingTableForm.unit').d('Unit')} - {maxLabel} {intl.get('host.pricingTableForm.unit').d('Unit')}
      </div>
    );
  };

  return (
    <>
      {selectedPricingTable._id ? (
        <div className={styles.pricingGridContainer}>
          <div style={{ marginBottom: '8px' }}>
            <label>{intl.get('host.pricingTableForm.pricingTableName').d('Pricing Table Name')}: </label>
            <span>{selectedPricingTable.name}</span>
          </div>

          {/* Split layout: fixed left column + scrollable right columns */}
          <div className={styles.tableWrapper}>
            {/* Fixed left — Package column */}
            <div className={styles.fixedCol}>
              <table className={styles.fixedTable}>
                <thead>
                  <tr>
                    <th className={styles.pricingGridHeader} style={{ width: LABEL_COL_WIDTH, padding: 0 }}>
                      <div className={styles.diagonalHeader}>
                        <span className={styles.productLabel}>{intl.get('host.pricingTableForm.package').d('Package')}</span>
                        <span className={styles.unitLabel}>{intl.get('host.pricingTableForm.unit').d('Unit')}</span>
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map(productIndex => (
                    <tr key={productIndex}>
                      <td className={styles.pricingGridItem} style={{ width: LABEL_COL_WIDTH }}>
                        {getProductName(productIndex)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Scrollable right — tier columns */}
            <div className={styles.scrollableCol}>
              <table className={styles.scrollableTable}>
                <colgroup>
                  {columns.map((_, i) => (
                    <col key={i} style={{ width: MIN_COL_WIDTH, minWidth: MIN_COL_WIDTH }} />
                  ))}
                </colgroup>
                <thead>
                  <tr>
                    {columns.map((tierKey, i) => (
                      <th key={i} className={styles.pricingGridHeader} style={{ minWidth: MIN_COL_WIDTH, textAlign: 'center' }}>
                        {getTierHeader(tierKey)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map(productIndex => (
                    <tr key={productIndex}>
                      {columns.map((_, colIndex) => (
                        <td key={`pricing-${productIndex}-${colIndex}`} className={styles.pricingGridItem} style={{ minWidth: MIN_COL_WIDTH }}>
                          {getPricingCell(productIndex, colIndex)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className={styles.label}>{intl.get('host.pricingTableForm.pricingTable').d('Pricing Table')}</div>
          <div className={styles.noPricingTable}>{intl.get('host.pricingTableForm.noPricingTable').d('No Pricing Table Applied')}</div>
        </>
      )}
    </>
  );
};

export default PricingTableHostForm;
